/**
 * Kurateret desktop-hovednavigation (navy-800-række) med megadropdowns.
 *
 * Bygger på det rene nav-modul (buildNav/NAV_CONFIG) i stedet for rå
 * Shopify-kollektionstitler. Radix NavigationMenu giver tastaturnavigation
 * og hover/fokus-åbning; klik på selve hovedpunktets label navigerer til
 * punktets primære kollektion (Enter/Space åbner panelet — deri ligger
 * "Alle i …"-linket).
 */
import { useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useCollections } from "@/features/catalog/hooks/useCatalog";
import { useAllowedProductIds } from "@/features/catalog/hooks/useAllowedProducts";
import {
  accessibleCollectionIdSet,
  buildNav,
  findNavItemForCollection,
  type ResolvedNavItem,
  type ResolvedNavLink,
} from "@/features/catalog/lib/nav";
import { cn } from "@/lib/utils";

function collectionUrl(link: ResolvedNavLink): string {
  return `/produkter?kollektion=${encodeURIComponent(link.collectionId)}`;
}

/** Blåt link i megapanelet: kurateret titel + diskret antal. */
function PanelLink({ link }: { link: ResolvedNavLink }) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={collectionUrl(link)}
          className="group/link flex items-baseline justify-between gap-3 rounded px-1.5 py-[5px] text-sm font-medium text-primary hover:bg-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 leading-snug">{link.label}</span>
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
            {link.productCount}
          </span>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}

/** Ét hovedpunkt: trigger (label + chevron) + megapanel med gruppekolonner. */
function NavItem({ item, active }: { item: ResolvedNavItem; active: boolean }) {
  const navigate = useNavigate();
  const columns = Math.max(item.groups.length, 1);

  return (
    <NavigationMenuItem value={item.id}>
      <NavigationMenuTrigger
        onClick={(e) => {
          // Museklik på labelen navigerer til den primære kollektion;
          // tastatur (detail === 0) beholder Radix' åbn/luk af panelet.
          if (e.detail > 0) {
            e.preventDefault();
            navigate(collectionUrl(item.link));
          }
        }}
        className={cn(
          "h-11 gap-1 rounded-none bg-transparent px-3 text-sm font-semibold",
          "text-navy-100 hover:bg-white/5 hover:text-white focus:bg-white/5 focus:text-white",
          "data-[state=open]:bg-white/10 data-[state=open]:text-white",
          active && "text-white shadow-[inset_0_-2px_0_0_hsl(var(--cta))]",
        )}
      >
        {item.label}
        {item.badge && (
          <span
            aria-hidden
            className="ml-0.5 rounded-full bg-cta px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wide text-white"
          >
            {item.badge}
          </span>
        )}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div
          className={cn(
            "p-5",
            columns === 1 && "w-[300px]",
            columns === 2 && "w-[480px]",
            columns === 3 && "w-[700px]",
            columns >= 4 && "w-[900px]",
          )}
        >
          <div className="mb-3 flex items-baseline justify-between gap-4 border-b border-border pb-2.5">
            <NavigationMenuLink asChild>
              <Link
                to={collectionUrl(item.link)}
                className="text-sm font-bold text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Alle i {item.label} →
              </Link>
            </NavigationMenuLink>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {item.link.productCount} varer
            </span>
          </div>
          <div
            className="grid gap-x-8 gap-y-5"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {item.groups.map((group, gi) => (
              <div key={group.heading ?? gi} className="min-w-0">
                {group.heading && (
                  <p className="mb-1.5 px-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                    {group.heading}
                  </p>
                )}
                <ul className="space-y-px">
                  {group.links.map((link) => (
                    <PanelLink key={link.collectionId} link={link} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

/** Ren visning af nav-rækken (testbar uden data-hooks). */
export function HeaderNavView({
  nav,
  activeItemId,
  isAllProducts,
}: {
  nav: ResolvedNavItem[];
  activeItemId: string | null;
  isAllProducts: boolean;
}) {
  const [menuValue, setMenuValue] = useState("");
  return (
    <nav aria-label="Hovednavigation" className="hidden bg-navy-800 md:block">
      <div className="mx-auto flex max-w-[1400px] items-center gap-1 px-4">
        <NavigationMenu
          value={menuValue}
          onValueChange={setMenuValue}
          className="max-w-none justify-start"
        >
          <NavigationMenuList className="justify-start space-x-0">
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <NavLink
                  to="/produkter"
                  end
                  className={cn(
                    "flex h-11 items-center whitespace-nowrap px-3 text-sm font-semibold transition-colors",
                    isAllProducts
                      ? "text-white shadow-[inset_0_-2px_0_0_hsl(var(--cta))]"
                      : "text-navy-100 hover:text-white",
                  )}
                >
                  Alle varer
                </NavLink>
              </NavigationMenuLink>
            </NavigationMenuItem>
            {nav.map((item) => (
              <NavItem key={item.id} item={item} active={activeItemId === item.id} />
            ))}
          </NavigationMenuList>
        </NavigationMenu>
        <div className="flex-1" />
        <NavLink
          to="/hurtig"
          className={({ isActive }) =>
            cn(
              "flex min-h-11 items-center gap-1.5 px-3 text-sm font-bold transition-colors",
              isActive ? "text-white" : "text-navy-100 hover:text-white",
            )
          }
        >
          <Zap className="h-4 w-4" aria-hidden />
          Hurtig bestilling
        </NavLink>
      </div>
    </nav>
  );
}

/** Nav-rækken (kun desktop): kobler katalog- og adgangsdata på visningen. */
export function HeaderNav() {
  const { data: collections } = useCollections();
  const allowedIds = useAllowedProductIds();
  const location = useLocation();

  const nav = useMemo(() => {
    if (!collections) return [];
    const accessible =
      allowedIds === null ? undefined : accessibleCollectionIdSet(collections, allowedIds);
    return buildNav(collections, accessible);
  }, [collections, allowedIds]);

  // Aktivt hovedpunkt ud fra ?kollektion= på produktsiden
  const activeItemId = useMemo(() => {
    if (location.pathname !== "/produkter") return null;
    const id = new URLSearchParams(location.search).get("kollektion");
    if (!id) return null;
    return findNavItemForCollection(nav, id)?.id ?? null;
  }, [location.pathname, location.search, nav]);

  const isAllProducts =
    location.pathname === "/produkter" &&
    !new URLSearchParams(location.search).get("kollektion");

  return <HeaderNavView nav={nav} activeItemId={activeItemId} isAllProducts={isAllProducts} />;
}
